import { useAuth } from 'context/auth-context';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { KiloClassAthleteAgenda, KiloClassAthleteRetrieve } from 'types';

dayjs.extend(utc);
dayjs.extend(timezone);

// classData.startTime is a gym-local wall-clock string ("HH:mm:ss") with no
// timezone offset. Parsing it with plain `Date.parse` interprets it in the
// browser's local TZ, which drifts away from gym time for traveling athletes
// (the events list is server-filtered by gym TZ, so the two would disagree).
// Anchor both `now` and the event start to the gym's TZ.
function getEventStartTime(
  classData: KiloClassAthleteAgenda | KiloClassAthleteRetrieve,
  gymTimezone: string | undefined,
): Dayjs {
  const wallClock = `${classData.startDate}T${classData.startTime}`;
  return gymTimezone ? dayjs.tz(wallClock, gymTimezone) : dayjs(Date.parse(wallClock));
}

function calculateWindowTime(eventStart: Dayjs, interval?: string, intervalCount?: number): Dayjs {
  if (!interval || !intervalCount) {
    return eventStart;
  }
  return eventStart.subtract(intervalCount, interval as any);
}

export type UseClassTimingReturn = {
  eventStartDateTime: Dayjs;
  hasStarted: boolean;
  isWithinReservationWindow: boolean;
  isWithinCancellationWindow: boolean;
  isWithinLateCancellationWindow: boolean;
};

export function useClassTiming(
  classData: KiloClassAthleteAgenda | KiloClassAthleteRetrieve | undefined,
): UseClassTimingReturn {
  const { session } = useAuth();
  const gymTimezone = session?.gym?.timezone;

  if (!classData) {
    return {
      eventStartDateTime: dayjs(),
      hasStarted: false,
      isWithinReservationWindow: false,
      isWithinCancellationWindow: false,
      isWithinLateCancellationWindow: false,
    };
  }

  const now = gymTimezone ? dayjs().tz(gymTimezone) : dayjs();
  const eventStartDateTime = getEventStartTime(classData, gymTimezone);
  const settings = classData.reservationAndCancellationSettings;

  const hasStarted = now.isAfter(eventStartDateTime);

  // Reservation window
  const reservationWindowOpen = calculateWindowTime(
    eventStartDateTime,
    settings?.reservationWindowOpenInterval,
    settings?.reservationWindowOpenIntervalCount,
  );

  const reservationWindowClose = calculateWindowTime(
    eventStartDateTime,
    settings?.reservationWindowCloseInterval,
    settings?.reservationWindowCloseIntervalCount,
  );

  const isWithinReservationWindow =
    now.isAfter(reservationWindowOpen) && now.isBefore(reservationWindowClose);

  // Cancellation window
  const cancellationDeadline = calculateWindowTime(
    eventStartDateTime,
    settings?.cancellationInterval,
    settings?.cancellationIntervalCount,
  );

  const isWithinCancellationWindow = now.isBefore(cancellationDeadline);

  const isWithinLateCancellationWindow = settings.allowLateCancellation
    ? now.isAfter(cancellationDeadline) && now.isBefore(eventStartDateTime)
    : false;

  return {
    eventStartDateTime,
    hasStarted,
    isWithinReservationWindow,
    isWithinCancellationWindow,
    isWithinLateCancellationWindow,
  };
}
